import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import Modal from "react-modal";
import { X, FileText, Calendar } from "lucide-react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MasterDataModal from "./MasterDataModal";
import { useSalesFormStore, useRestoredSalesForm } from "../services/useSalesFormStore";
import { useDraftForm } from "../context/drafts/DraftForm";
import SelectTransaction from "./SelectTransaction";
import FilePreview from "./FilePreview";
import { displayToast } from "../utils/Toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { uploadFile } from "../utils/uploadFile";
import { deleteAttachment, fetchAttachments } from "../apis/attachments";
import InspectionCardPopup from "./InspectionCardPopup";
import { useOutsideClick } from "../utils/useOutsideClick";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import { resolveValidationFieldUpdate } from "../utils/validationFieldUpdate";
import {
  createMaterialRequest,
  editTransactionByCode,
  fetchTransactionPopupData,
  fetchLastHeadData,
  fetchEmployeesList,
} from "../apis/transaction";
import { fetchTransactionNo } from "../apis/transaction";
import "../styles/components/PrimaryDocument.css";

const AnalysisDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      className="analysis-date-input-box"
      onClick={onClick}
      ref={ref}
      tabIndex={0}
    >
      <span
        className={value ? "analysis-date-value" : "analysis-date-placeholder"}
      >
        {value || placeholder}
      </span>
      <Calendar size={16} className="analysis-date-icon" />
    </div>
  ),
);

const PrimaryDateCalendar = ({ children, onClear, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer {...props}>{children}</CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="primary-date-calendar-clear"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const CALENDAR_MODIFIERS = [
  {
    name: "shiftY",
    fn({ y, placement }) {
      if (String(placement).startsWith("top")) {
        return { y: Math.max(10, y) };
      }
      return {};
    },
  },
];

const mergeFieldAttachments = (savedAttachments, pendingAttachments) => {
  const saved = savedAttachments || [];
  const pending = pendingAttachments || [];
  const savedIds = new Set(
    saved
      .map((attachment) => attachment.id ?? attachment.attachment_id)
      .filter((id) => id != null),
  );
  return [
    ...saved,
    ...pending.filter((attachment) => {
      const id = attachment.id ?? attachment.attachment_id;
      return id == null || !savedIds.has(id);
    }),
  ];
};

const PrimaryDocument = ({
  headFields,
  transactionCode,
  extraForms,
  isViewMode = false,
  isCopyMode = false,
  isInEditMode = false,
  backDatedEntry,
  isAutoTransactionNo,
  modeHighlight,
  type = "",
}) => {
  const inputRef = useRef(null);
  const inputRefs = useRef({});
  const hasInitializedForm = useRef(false);
  const hasFetchedFixedValues = useRef(false);
  const hasFetchedTranNo = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clearedDateFields, setClearedDateFields] = useState(() => new Set());
  const [popupItems, setPopupItems] = useState([]);
  const [matchedHeadFields, setMatchedHeadFields] = useState([]);
  const [showInspectionPopup, setShowInspectionPopup] = useState(false);
  const [isOtherDetailsModalOpen, setIsOtherDetailsModal] = useState(false);
  const [inspectionPopupState, setInspectionPopupState] = useState({
    final_inspection: false,
    in_process_inspection: false,
    first_piece_inspection: false,
  });
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const dropdownRefs = useRef({});
  const dropdownMenuRefs = useRef({});
  const containerRef = useRef();
  const contentRef = useRef();
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMaterialRequested, setIsMaterialRequested] = useState(false);
  const [selectedInspectionFieldName, setSelectedInspectionFieldName] =
    useState("");
  const [selectedFileNames, setSelectedFileNames] = useState({});
  const [headAttachmentsByField, setHeadAttachmentsByField] = useState({});
  const [pendingHeadAttachmentsByField, setPendingHeadAttachmentsByField] =
    useState({});
  const [currentQueryField, setCurrentQueryField] = useState(null);
  const [queryFieldData, setQueryFieldData] = useState({});
  const [engineerOptions, setEngineerOptions] = useState([]);
  const hasFetchedEngineerOptions = useRef(false);
  const [popupSearchQuery, setPopupSearchQuery] = useState("");
  const popUpModalRef = useOutsideClick(() => {
    setIsPopupOpen(false);
    setCurrentQueryField(null);
  });
  const formData = useSalesFormStore((state) => state.formData);
  const restoredSnapshot = useRestoredSalesForm();
  const setPrimaryDetails = useSalesFormStore(
    (state) => state.setPrimaryDetails,
  );
  const addStagedAttachments = useSalesFormStore(
    (state) => state.addStagedAttachments,
  );
  const removeStagedAttachments = useSalesFormStore(
    (state) => state.removeStagedAttachments,
  );
  const clearStagedAttachments = useSalesFormStore(
    (state) => state.clearStagedAttachments,
  );
  const stagedAttachments = useSalesFormStore(
    (state) => state.stagedAttachments,
  );
  const routerLocation = useLocation();
  const draftForm = useDraftForm();
  const location = draftForm ? { ...routerLocation, state: draftForm.navigationState } : routerLocation;
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const productData = location.state?.productData ?? {};
  const workOrderNoFromNavigation = location.state?.woNo ?? "";
  const initializeForm = useSalesFormStore((state) => state.initializeForm);
  const detailButtons = extraForms.map((item) => ({
    id: item.id,
    name: item.name,
    title: item.title,
  }));

  const jobCardCreated = useMemo(() => {
    return (
      formData?.primaryDetails?.status === "started" &&
      formData?.transactionItems?.some(
        (item) => item.jobcard_start === "started",
      )
    );
  }, [formData?.primaryDetails, formData?.transactionItems]);

  const publicHeadFields = useMemo(() => {
    return [...headFields]
      .filter((field) => !field.new_form_id || field.new_form_id === 0)
      .sort((a, b) => a.order_no - b.order_no);
  }, [headFields]);

  useEffect(() => {
    const hasAssignedEngineerField = publicHeadFields.some(
      (field) => field.field_name === "assigned_engineer",
    );
    if (!hasAssignedEngineerField || hasFetchedEngineerOptions.current) return;
    hasFetchedEngineerOptions.current = true;

    const loadEngineerOptions = async () => {
      try {
        const response = await fetchEmployeesList("engineer");
        const employees =
          response?.items ||
          response?.data ||
          (Array.isArray(response) ? response : []);
        const names = employees
          .map((employee) =>
            String(
              employee?.name ||
                employee?.employee_name ||
                employee?.emp_name ||
                "",
            ).trim(),
          )
          .filter(Boolean);
        setEngineerOptions([...new Set(names)]);
      } catch (error) {
        console.error("Failed to fetch engineer employees:", error);
      }
    };

    loadEngineerOptions();
  }, [publicHeadFields]);

  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  useEffect(() => {
    return () => {
      if (!restoredSnapshot && !isViewMode && !formData.primaryDetails?.tran_id) {
        clearStagedAttachments();
      }
    };
  }, [clearStagedAttachments, formData.primaryDetails?.tran_id, isViewMode, restoredSnapshot]);

  useEffect(() => {
    const parentId = formData.primaryDetails?.tran_id;
    if (
      (!isViewMode && !isInEditMode) ||
      isCopyMode ||
      !parentId ||
      !transactionCode
    ) {
      setHeadAttachmentsByField({});
      return;
    }

    const loadHeadAttachments = async () => {
      try {
        const response = await fetchAttachments({
          tranCode: transactionCode,
          scope: "head",
          parentId,
        });
        const attachments = Array.isArray(response)
          ? response
          : response?.attachments || response?.items || response?.data || [];
        const groupedAttachments = attachments.reduce((groups, attachment) => {
          const fieldName = attachment.field_name;
          if (fieldName) {
            groups[fieldName] = [...(groups[fieldName] || []), attachment];
          }
          return groups;
        }, {});
        setHeadAttachmentsByField(groupedAttachments);
      } catch (error) {
        console.error("Failed to load head attachments:", error);
        setHeadAttachmentsByField({});
      }
    };

    loadHeadAttachments();
  }, [
    formData.primaryDetails?.tran_id,
    isViewMode,
    isInEditMode,
    isCopyMode,
    transactionCode,
  ]);

  useEffect(() => {
    const getTransactionNumber = async () => {
      try {
        const response = await fetchTransactionNo(transactionCode);
        const transactionNo = response?.TransactionNumber;
        console.log(
          "[DEBUG] fetchTransactionNo response:",
          transactionCode,
          response,
        );

        if (transactionNo) {
          setPrimaryDetails({ tran_no: transactionNo });
        }
      } catch (error) {
        console.error("Failed to fetch transaction number:", error);
      }
    };

    console.log("[DEBUG] tran_no useEffect conditions:", {
      isViewMode,
      isCopyMode,
      isInEditMode,
      transactionCode,
      isAutoTransactionNo,
    });

    if (
      !restoredSnapshot &&
      !isViewMode &&
      !isCopyMode &&
      !isInEditMode &&
      transactionCode &&
      isAutoTransactionNo
    ) {
      getTransactionNumber();
    }
  }, [
    transactionCode,
    isViewMode,
    isCopyMode,
    isInEditMode,
    isAutoTransactionNo,
    restoredSnapshot,
    setPrimaryDetails,
  ]);

  const handlefetchTransactionPopupData = async (dbQuery, fieldName = null) => {
    try {
      const response = await fetchTransactionPopupData(dbQuery);
      if (response.message) {
        displayToast(response.message, "error");
      } else {
        if (response.items) {
          if (fieldName) {
            setQueryFieldData((prev) => ({
              ...prev,
              [fieldName]: response.items || [],
            }));
          } else {
            setPopupItems(response.items || []);
            setIsPopupOpen(true);
          }
        }
      }
    } catch (error) {
      displayToast("Failed to fetch transaction data", "error");
    }
  };

  useEffect(() => {
    if (Object.keys(formData.primaryDetails).length === 0) {
      console.log(
        "[DEBUG] PrimaryDoc: primaryDetails is empty, resetting hasInitializedForm",
      );
      hasInitializedForm.current = false;
      hasFetchedFixedValues.current = false;
      hasFetchedTranNo.current = false;
    }
  }, [formData.primaryDetails]);

  useEffect(() => {
    console.log("[DEBUG] PrimaryDoc INIT useEffect", {
      publicHeadFieldsCount: publicHeadFields.length,
      hasInitialized: hasInitializedForm.current,
      primaryDetailsKeys: Object.keys(formData.primaryDetails).length,
    });
    if (
      publicHeadFields.length > 0 &&
      !restoredSnapshot &&
      !hasInitializedForm.current &&
      Object.keys(formData.primaryDetails).length === 0
    ) {
      console.log(
        "[DEBUG] PrimaryDoc: INITIALIZING FORM with empty field values",
      );
      hasInitializedForm.current = true;
      const initialData = {};
      publicHeadFields.forEach((field) => {
        initialData[field.field_name] = field.field_type === "DATE" ? null : "";
      });
      if (productData) {
        initialData.product_id = productData.tran_id;
        initialData.product_name = productData.name;
        initialData.product_desc = productData.description;
      }
      if (transactionCode === "PL" && workOrderNoFromNavigation) {
        initialData.design_workorder = workOrderNoFromNavigation;
      }
      initializeForm(initialData);
      console.log(
        "[DEBUG] PrimaryDoc: initializeForm called with",
        Object.keys(initialData).length,
        "fields",
      );
    }
  }, [
    publicHeadFields,
    initializeForm,
    restoredSnapshot,
    formData.primaryDetails,
    productData,
    transactionCode,
    workOrderNoFromNavigation,
  ]);

  useEffect(() => {
    if (
      transactionCode === "PL" &&
      workOrderNoFromNavigation &&
      formData?.primaryDetails?.design_workorder !== workOrderNoFromNavigation
    ) {
      setPrimaryDetails({ design_workorder: workOrderNoFromNavigation });
    }
  }, [
    transactionCode,
    workOrderNoFromNavigation,
    formData?.primaryDetails?.design_workorder,
    setPrimaryDetails,
  ]);

  useEffect(() => {
    if (
      publicHeadFields.length > 0 &&
      !restoredSnapshot &&
      !isViewMode &&
      !isInEditMode &&
      !isCopyMode &&
      !hasFetchedFixedValues.current
    ) {
      const fixedFields = publicHeadFields
        .filter((f) => f.fixed_value)
        .filter((f) => !(f.field_name === "tran_no" && isAutoTransactionNo))
        .map((f) => f.field_name);

      if (fixedFields.length > 0) {
        hasFetchedFixedValues.current = true;
        console.log(
          "[DEBUG] PrimaryDoc: Fetching fixed_value fields:",
          fixedFields,
        );
        fetchLastHeadData(transactionCode, fixedFields)
          .then((response) => {
            console.log(
              "[DEBUG] PrimaryDoc: fetchLastHeadData response:",
              response,
            );
            if (response && typeof response === "object") {
              const retainedUpdates = {};
              fixedFields.forEach((fieldName) => {
                if (
                  response[fieldName] !== undefined &&
                  response[fieldName] !== null &&
                  response[fieldName] !== "" &&
                  response[fieldName] !== 0
                ) {
                  retainedUpdates[fieldName] = String(response[fieldName]);
                }
              });
              if (Object.keys(retainedUpdates).length > 0) {
                console.log(
                  "[DEBUG] PrimaryDoc: Applying fixed_value updates:",
                  retainedUpdates,
                );
                setPrimaryDetails(retainedUpdates);
              }
            }
          })
          .catch((err) => {
            console.error("[DEBUG] PrimaryDoc: fetchLastHeadData error:", err);
            hasFetchedFixedValues.current = false;
          });
      }
    }
  }, [
    publicHeadFields,
    transactionCode,
    isViewMode,
    isInEditMode,
    isCopyMode,
    setPrimaryDetails,
    restoredSnapshot,
    isAutoTransactionNo,
  ]);

  useEffect(() => {
    const details = formData?.primaryDetails;

    if (details?.tran_no && type === "JobCard") {
      const updatedInspectionState = {
        final_inspection: details.final_inspection === "created",
        in_process_inspection: details.in_process_inspection === "created",
        first_piece_inspection: details.first_piece_inspection === "created",
      };

      setInspectionPopupState(updatedInspectionState);
    }
  }, [formData.primaryDetails, type]);

  const handleClick = (detail) => {
    const visibleHeadFields = headFields.filter(
      (field) => !field.is_internal_field,
    );
    const matchedFields = visibleHeadFields.filter(
      (field) => field.new_form_id === detail.id,
    );

    if (matchedFields.length > 0) {
      setMatchedHeadFields(matchedFields);
    } else {
      console.warn("No matching public head fields found");
      setMatchedHeadFields([]);
    }

    openOtherDetailsModal(detail);
  };

  const handleInputChange = (e) => {
    if (isViewMode) return;
    const { name, value } = e.target;
    setPrimaryDetails({ [name]: value });
  };

  const handleDateChange = (date, fieldName) => {
    if (isViewMode) return;
    setClearedDateFields((previous) => {
      const updated = new Set(previous);
      if (date) {
        updated.delete(fieldName);
      } else {
        updated.add(fieldName);
      }
      return updated;
    });
    setPrimaryDetails({ [fieldName]: date });
  };

  const handleImageChange = async (e, fieldName) => {
    if (isViewMode) return;

    const files = e.target.files;
    const file = files[0];
    if (file) {
      const uploadBatchId = `pending-${Date.now()}-${fieldName}`;
      const selectedFileNames = Array.from(
        files,
        (selectedFile) => selectedFile.name,
      );
      const selectedAttachments = Array.from(files, (selectedFile) => ({
        uploadBatchId,
        file_name: selectedFile.name,
      }));
      setSelectedFileNames((previous) => ({
        ...previous,
        [fieldName]: [
          ...(Array.isArray(previous[fieldName])
            ? previous[fieldName]
            : previous[fieldName]
              ? [previous[fieldName]]
              : []),
          ...selectedFileNames,
        ],
      }));
      setPendingHeadAttachmentsByField((previous) => ({
        ...previous,
        [fieldName]: [...(previous[fieldName] || []), ...selectedAttachments],
      }));
      try {
        const uploadResponse = await uploadFile(files, "transaction", {
          tranCode: transactionCode,
          scope: "head",
          fieldName,
          parentId: formData.primaryDetails?.tran_id,
        });

        const imageUrl = uploadResponse?.url;

        if (!imageUrl) {
          displayToast("Upload failed - no URL returned", "error");
          return;
        }
        setPrimaryDetails({ [fieldName]: imageUrl });
        if (!formData.primaryDetails?.tran_id && uploadResponse?.attachments) {
          addStagedAttachments(
            uploadResponse.attachments.map((attachment) => ({
              ...attachment,
              scope: "head",
              fieldName,
            })),
          );
        }
        if (uploadResponse?.attachments) {
          setPendingHeadAttachmentsByField((previous) => ({
            ...previous,
            [fieldName]: [
              ...(previous[fieldName] || []).filter(
                (attachment) => attachment.uploadBatchId !== uploadBatchId,
              ),
              ...uploadResponse.attachments,
            ],
          }));
          setHeadAttachmentsByField((previous) => ({
            ...previous,
            [fieldName]: [
              ...(previous[fieldName] || []),
              ...uploadResponse.attachments,
            ],
          }));
        }

        displayToast("Image uploaded successfully", "success");
      } catch (error) {
        setPendingHeadAttachmentsByField((previous) => ({
          ...previous,
          [fieldName]: (previous[fieldName] || []).filter(
            (attachment) => attachment.uploadBatchId !== uploadBatchId,
          ),
        }));
        displayToast("Failed to upload image", "error");

        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    }
  };

  const handleRemoveImage = (fieldName) => {
    setPrimaryDetails({
      [fieldName]: null,
    });
    setSelectedFileNames((prev) => {
      const newState = { ...prev };
      delete newState[fieldName];
      return newState;
    });

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemovePendingAttachment = async (fieldName, attachment) => {
    const attachmentId = attachment.id ?? attachment.attachment_id;

    if (attachmentId != null) {
      try {
        await deleteAttachment(attachmentId);
      } catch (error) {
        displayToast("Failed to delete file", "error");
        return;
      }
    }
    removeStagedAttachments(attachmentId != null ? [attachmentId] : []);
    setPrimaryDetails({ [fieldName]: null });
    setHeadAttachmentsByField((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] || []).filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      ),
    }));
    setPendingHeadAttachmentsByField((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] || []).filter(
        (currentAttachment) =>
          (currentAttachment.id ?? currentAttachment.attachment_id) !==
          attachmentId,
      ),
    }));
    setSelectedFileNames((previous) => ({
      ...previous,
      [fieldName]: (previous[fieldName] || []).filter(
        (fileName) => fileName !== (attachment.file_name || attachment.name),
      ),
    }));
  };

  const handleFileChange = async (e, fieldName) => {
    if (isViewMode) return;

    const files = e.target.files;
    const file = files[0];
    if (file) {
      const uploadBatchId = `pending-${Date.now()}-${fieldName}`;
      const selectedFileNames = Array.from(
        files,
        (selectedFile) => selectedFile.name,
      );
      const selectedAttachments = Array.from(files, (selectedFile) => ({
        uploadBatchId,
        file_name: selectedFile.name,
      }));
      setSelectedFileNames((previous) => ({
        ...previous,
        [fieldName]: [
          ...(Array.isArray(previous[fieldName])
            ? previous[fieldName]
            : previous[fieldName]
              ? [previous[fieldName]]
              : []),
          ...selectedFileNames,
        ],
      }));
      setPendingHeadAttachmentsByField((previous) => ({
        ...previous,
        [fieldName]: [...(previous[fieldName] || []), ...selectedAttachments],
      }));
      try {
        const uploadResponse = await uploadFile(files, "transaction", {
          tranCode: transactionCode,
          scope: "head",
          fieldName,
          parentId: formData.primaryDetails?.tran_id,
        });
        const fileUrl = uploadResponse?.url;

        if (!fileUrl) {
          displayToast("Upload failed - no URL returned", "error");
          return;
        }
        setPrimaryDetails({ [fieldName]: fileUrl });
        if (!formData.primaryDetails?.tran_id && uploadResponse?.attachments) {
          addStagedAttachments(
            uploadResponse.attachments.map((attachment) => ({
              ...attachment,
              scope: "head",
              fieldName,
            })),
          );
        }
        if (uploadResponse?.attachments) {
          setPendingHeadAttachmentsByField((previous) => ({
            ...previous,
            [fieldName]: [
              ...(previous[fieldName] || []).filter(
                (attachment) => attachment.uploadBatchId !== uploadBatchId,
              ),
              ...uploadResponse.attachments,
            ],
          }));
          setHeadAttachmentsByField((previous) => ({
            ...previous,
            [fieldName]: [
              ...(previous[fieldName] || []),
              ...uploadResponse.attachments,
            ],
          }));
        }

        displayToast("File uploaded successfully", "success");
      } catch (error) {
        setPendingHeadAttachmentsByField((previous) => ({
          ...previous,
          [fieldName]: (previous[fieldName] || []).filter(
            (attachment) => attachment.uploadBatchId !== uploadBatchId,
          ),
        }));
        displayToast("Failed to upload file", "error");

        if (inputRefs.current[fieldName]) {
          inputRefs.current[fieldName].value = "";
        }
      }
    }
  };

  const handleTransaction = () => {
    if (!isViewMode) {
      setIsModalOpen(true);
    }
  };

  const handleCreateBom = () => {
    console.log("BOM clicked, navigating to:", `/${tab}/${category}/bom`);

    const bomProductData =
      productData && Object.keys(productData).length > 0
        ? productData
        : {
            tran_id: formData?.primaryDetails?.product_id,
            name: formData?.primaryDetails?.product_name,
            description: formData?.primaryDetails?.product_desc,
          };

    navigate(`/${tab}/${category}/bom`, {
      state: {
        ...location.state,
        productData: bomProductData,
        company: location.state?.company,
        transactionId: formData?.primaryDetails?.tran_id,
        transactionCode: transactionCode,
        headFields: headFields,
        extraForms: extraForms,
        primaryDetails: formData?.primaryDetails,
        transactionItems: formData?.transactionItems,
        customerDetails: formData?.customerDetails,
        isViewMode: isViewMode,
        isEditMode: isInEditMode,
        isCopyMode: isCopyMode,
      },
    });
  };

  const handleCreatePackingList = () => {
    const isAlreadyCreated =
      formData?.primaryDetails?.packing_list === "success";

    const woTranId =
      formData?.primaryDetails?.tran_id || productData?.tran_id || null;

    const packingListProductData = {
      tran_id: formData?.primaryDetails?.product_id || woTranId,
      name: formData?.primaryDetails?.product_name || productData?.name || "",
      description:
        formData?.primaryDetails?.product_desc ||
        productData?.description ||
        "",
      pl_created: isAlreadyCreated ? "success" : undefined,
    };

    const woNo = formData?.primaryDetails?.tran_no || "";

    navigate(`/${tab}/${category}/packing-list`, {
      state: {
        productData: packingListProductData,
        isViewMode: isAlreadyCreated,
        woNo,
        fromTransactionDetail: true,
        returnTransactionId: woTranId,
        returnTransactionCode: transactionCode,
        returnLocationState: location.state || {},
      },
    });
  };

  const handleRemoveFile = (fieldName) => {
    setPrimaryDetails({
      [fieldName]: null,
    });

    setSelectedFileNames((prev) => {
      const newState = { ...prev };
      delete newState[fieldName];
      return newState;
    });

    if (inputRefs.current[fieldName]) {
      inputRefs.current[fieldName].value = "";
    }
  };

  const openOtherDetailsModal = (detail) => {
    setIsOtherDetailsModal(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const closeOtherDetailsModal = () => setIsOtherDetailsModal(false);

  const formatFieldValue = (value, fieldType) => {
    if (!value) return "-";
    const truncateFileName = (name, maxLength = 20) => {
      if (!name) return "";
      if (name.length <= maxLength) return name;

      const extIndex = name.lastIndexOf(".");
      const ext = extIndex !== -1 ? name.substring(extIndex) : "";
      const baseLength = maxLength - ext.length - 3;

      if (baseLength <= 0) {
        return `...${ext}`;
      }

      const base = name.substring(0, baseLength);
      return `${base}...${ext}`;
    };

    switch (fieldType) {
      case "DATE":
      case "DATETIME":
        return new Date(value).toLocaleDateString();
      case "DECIMAL":
        return parseFloat(value).toFixed(2);
      case "IMAGE":
        return <FilePreview url={value} />;
      case "file_upload":
        return <FilePreview url={value} />;
      default:
        return value;
    }
  };

  const handleEnterValidation = async (field) => {
    if (!field.enter_validation) return;

    if (field.enter_validation.is_db_query && field.enter_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.enter_validation.db_query,
        );

        if (response.items && field.enter_validation.field_updation) {
          const updatedFields = {};
          field.enter_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData.primaryDetails || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setPrimaryDetails(updatedFields);
          }
        }
      } catch (error) {
        console.error("Validation Error:", error);
      }
    }
  };

  const handleLeaveValidation = async (field) => {
    if (!field.leave_validation) return;

    if (field.leave_validation.is_db_query && field.leave_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.leave_validation.db_query,
        );

        if (response.items && field.leave_validation.field_updation) {
          const updatedFields = {};
          field.leave_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData.primaryDetails || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setPrimaryDetails(updatedFields);
          }
        }
      } catch (error) {
        console.error("Leave Validation Error:", error);
      }
    }
  };

  const handleFieldClick = (field) => {
    if (isViewMode) return;
    const hasQuery = field.is_query || field.is_db_query;
    if (hasQuery && field.db_query) {
      setCurrentQueryField(field.field_name);
      handlefetchTransactionPopupData(field.db_query);
    }
  };

  const handlePopupItemClick = (item, field) => {
    const value =
      item.Product_Name ||
      item.product_name ||
      item.name ||
      item.company_name ||
      item[field.field_name] ||
      "";
    const productId = item.id || item.tran_id || "";

    handleInputChange({
      target: {
        name: field.field_name,
        value: value,
      },
    });

    if (
      productId &&
      (field.field_name === "product_name" ||
        field.field_name.includes("product"))
    ) {
      handleInputChange({
        target: {
          name: "product_id",
          value: productId,
        },
      });
    }

    Object.keys(item).forEach((key) => {
      if (key !== field.field_name && key !== "id" && key !== "tran_id") {
        const matchingField = publicHeadFields.find(
          (f) => f.field_name === key,
        );
        if (matchingField) {
          handleInputChange({
            target: {
              name: key,
              value: item[key],
            },
          });
        }
      }
    });

    setIsPopupOpen(false);
    setCurrentQueryField(null);
    setPopupSearchQuery("");
  };

  const handleDropdownOpen = async (fieldName, field) => {
    if (isViewMode) return;
    if (openDropdownField === fieldName) {
      setOpenDropdownField(null);
      setCurrentQueryField(null);
      return;
    }

    const ref = dropdownRefs.current[fieldName];
    if (!ref?.current) {
      setOpenDropdownField(fieldName);
      return;
    }
    const triggerElement = ref.current;

    const hasQuery = field.is_query || field.is_db_query;
    if (hasQuery && field.db_query) {
      setCurrentQueryField(fieldName);
      await handlefetchTransactionPopupData(field.db_query, fieldName);
    } else {
      setCurrentQueryField(null);
    }

    setOpenDropdownField(fieldName);
    setDropdownStyle({
      position: "absolute",
      top: triggerElement.offsetTop + triggerElement.offsetHeight,
      left: triggerElement.offsetLeft,
      width: triggerElement.offsetWidth,
      zIndex: 1000,
    });
  };

  useEffect(() => {
    const tran_id = formData.primaryDetails?.tran_id;
    if (tran_id && formData.primaryDetails?.create_mr === "success") {
      setIsMaterialRequested(true);
    } else {
      setIsMaterialRequested(false);
    }
  }, [formData.primaryDetails?.tran_id, formData.primaryDetails?.create_mr]);

  const handleInspection = (fieldName) => {
    if (!jobCardCreated) {
      displayToast(
        "please start the job card process before creating inspection report",
        "error",
      );
      return;
    }

    setShowInspectionPopup(true);
    setSelectedInspectionFieldName(fieldName);
  };

  const renderedField = useMemo(() => {
    return (field) => {
      if (!field?.field_type || !field?.field_name) return null;

      const fieldValue = formData.primaryDetails[field.field_name];

      const isEditMode = formData.primaryDetails?.tran_no;
      const isFieldReadOnly = isEditMode && field.is_read_only_on_edit;

      const isDisabled = isViewMode || isFieldReadOnly;

      const isAssignedEngineerField = field.field_name === "assigned_engineer";

      if (field.field_name === "create_mr") {
        return null;
      }
      if (
        field.field_name === "final_inspection" ||
        field.field_name === "in_process_inspection" ||
        field.field_name === "first_piece_inspection"
      ) {
        const inspectionLabelMap = {
          final_inspection: "Final Inspection",
          in_process_inspection: "In-Process Inspection",
          first_piece_inspection: "First Piece Inspection",
        };

        const isInspectionDone =
          inspectionPopupState?.[field.field_name] === true;

        return (
          <div className="inspection-report-containers">
            <input type="text" className="custom-inputs" readOnly />
            <div className="inspection-action-buttons">
              <button
                type="button"
                className="custom-request-button fixed-width"
                onClick={() => handleInspection(field.field_name)}
                style={{
                  marginBottom: 6,
                  marginTop: 8,
                  cursor: !jobCardCreated ? "not-allowed" : "pointer",
                }}
              >
                {isInspectionDone
                  ? `View Inspection`
                  : inspectionLabelMap[field.field_name]}
              </button>
            </div>
          </div>
        );
      }

      if (field.is_internal_field) {
        return (
          <input
            type="hidden"
            name={field.field_name}
            value={fieldValue || ""}
            onChange={handleInputChange}
          />
        );
      }

      if (field.field_type === "BOM") {
        const isBomLinked = Boolean(
          formData?.primaryDetails?.bom || formData?.primaryDetails?.bom_number,
        );
        return (
          <button
            type="button"
            className="custom-request-button"
            onClick={handleCreateBom}
            style={{ marginBottom: 6, marginTop: 8 }}
          >
            {isBomLinked ? "BOM Created \u2713 \u00b7 View" : "Create BOM"}
          </button>
        );
      }

      if (field.field_type === "PACKING_LIST") {
        const isPackingListCreated =
          formData?.primaryDetails?.packing_list === "success";
        return (
          <button
            type="button"
            className="custom-request-button"
            onClick={handleCreatePackingList}
            style={{
              marginBottom: 6,
              marginTop: 8,
            }}
          >
            {isPackingListCreated ? "View Packing List" : "Packing List"}
          </button>
        );
      }

      if (isViewMode) {
        const isFileField = ["image", "file_upload", "case_file"].includes(
          String(field.field_type || "").toLowerCase(),
        );
        const attachments = headAttachmentsByField[field.field_name] || [];

        if (isFileField) {
          return (
            <div className="attachment-list attachment-list--view">
              {attachments.length ? (
                attachments.map((attachment) => (
                  <div
                    className="attachment-list__item"
                    key={attachment.id || attachment.attachment_id}
                  >
                    <FilePreview
                      url={
                        attachment.url ||
                        attachment.file_url ||
                        attachment.s3_url
                      }
                      fileName={attachment.file_name || attachment.name}
                    />
                  </div>
                ))
              ) : (
                <span className="attachment-list__empty">
                  No files attached
                </span>
              )}
            </div>
          );
        }

        if (field.field_type === "BOOLEAN") {
          return (
            <div>
              <input
                type="checkbox"
                checked={!!fieldValue}
                disabled
                className="master-data-modal-checkbox"
              />
            </div>
          );
        }
        if (field.field_type === "TEXT" || field.field_type === "text") {
          return (
            <div
              className="view-mode-field"
              style={{
                display: "block",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                overflowY: "auto",
                padding: "8px 0px",
                maxHeight: "5em",
                minHeight: "1.5em",
              }}
            >
              {fieldValue || "-"}
            </div>
          );
        }

        return (
          <div className="view-mode-field">
            {formatFieldValue(fieldValue, field.field_type)}
          </div>
        );
      }

      if (isFieldReadOnly) {
        if (field.field_type === "BOOLEAN") {
          return (
            <div>
              <input
                type="checkbox"
                checked={!!fieldValue}
                disabled
                className="master-data-modal-checkbox"
                style={{ cursor: "not-allowed" }}
              />
            </div>
          );
        }
        if (field.field_type === "TEXT" || field.field_type === "text") {
          return (
            <div className="read-only-field readonly-text-field">
              {fieldValue || "-"}
            </div>
          );
        }
        return (
          <div className="read-only-field">
            {formatFieldValue(fieldValue, field.field_type)}
          </div>
        );
      }

      if (
        !isAssignedEngineerField &&
        (field.field_type === "TEXT" || field.field_type === "text")
      ) {
        if (!inputRefs.current[field.field_name]) {
          inputRefs.current[field.field_name] = React.createRef();
        }
        const handleInput = (e) => {
          const textarea = e.target;
          textarea.style.height = "24px";
          const newHeight = Math.min(textarea.scrollHeight, 72);
          textarea.style.height = newHeight + "px";
          textarea.style.overflowY =
            textarea.scrollHeight > 72 ? "auto" : "hidden";
        };
        return (
          <textarea
            name={field.field_name}
            value={fieldValue || ""}
            onChange={(e) => {
              handleInputChange(e);
              handleInput(e);
            }}
            className="type-text-input"
            disabled={isDisabled}
            readOnly={isFieldReadOnly}
            placeholder={`Enter ${field.title || field.field_name}`}
            ref={inputRefs.current[field.field_name]}
            style={{
              height: "24px",
              overflowY: "hidden",
              backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
              cursor: isFieldReadOnly ? "not-allowed" : "text",
            }}
            onInput={handleInput}
            rows={1}
          />
        );
      }

      const isQueryDropdown =
        !isAssignedEngineerField &&
        (field.is_query || field.is_db_query) &&
        field.db_query;

      if (isQueryDropdown) {
        if (!dropdownRefs.current[field.field_name]) {
          dropdownRefs.current[field.field_name] = React.createRef();
        }
        if (!dropdownMenuRefs.current[field.field_name]) {
          dropdownMenuRefs.current[field.field_name] = React.createRef();
        }

        return (
          <div
            className="primary-dropdown-container"
            style={{ position: "relative" }}
          >
            <div
              className={`primary-dropdown-select ${
                isFieldReadOnly ? "read-only-dropdown" : ""
              }`}
              ref={dropdownRefs.current[field.field_name]}
              onClick={
                isFieldReadOnly
                  ? undefined
                  : () => handleDropdownOpen(field.field_name, field)
              }
              style={{
                backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                cursor: isFieldReadOnly ? "not-allowed" : "pointer",
              }}
            >
              <span style={{ color: !fieldValue ? "#6B7280" : "#444" }}>
                {fieldValue || `Select ${field.title}`}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name && !isDisabled && (
              <div
                className="primary-dropdown-options"
                style={dropdownStyle}
                ref={dropdownMenuRefs.current[field.field_name]}
              >
                {(queryFieldData[field.field_name] || []).map(
                  (option, index) => {
                    const value =
                      option?.Product_Name ||
                      option?.product_name ||
                      option?.name ||
                      option?.company_name ||
                      option?.[field.field_name] ||
                      "";

                    return (
                      <div
                        key={index}
                        className="primary-dropdown-option"
                        onClick={() => {
                          handleInputChange({
                            target: {
                              name: field.field_name,
                              value: value,
                            },
                          });

                          setOpenDropdownField(null);
                          setCurrentQueryField(null);
                        }}
                      >
                        {value}
                      </div>
                    );
                  },
                )}
              </div>
            )}
          </div>
        );
      }

      switch (isAssignedEngineerField ? "DROPDOWN" : field.field_type) {
        case "DATE":
        case "DATETIME":
          return (
            <DatePicker
              selected={
                fieldValue
                  ? new Date(fieldValue)
                  : field.field_name === "date" &&
                      !clearedDateFields.has(field.field_name)
                    ? new Date()
                    : null
              }
              onChange={
                isFieldReadOnly
                  ? undefined
                  : (date) => handleDateChange(date, field.field_name)
              }
              dateFormat="dd-MM-yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={26}
              dropdownMode="scroll"
              customInput={<AnalysisDateInput placeholder="Select a date" />}
              calendarContainer={(props) => (
                <PrimaryDateCalendar
                  {...props}
                  onClear={
                    isDisabled
                      ? undefined
                      : () => handleDateChange(null, field.field_name)
                  }
                />
              )}
              popperClassName="detail-datepicker-popper"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              popperModifiers={CALENDAR_MODIFIERS}
              portalId="datepicker-portal"
              placeholderText="Select a date"
              disabled={isDisabled}
              readOnly={isFieldReadOnly}
              minDate={!backDatedEntry ? new Date() : undefined}
              maxDate={field.field_name === "date" ? new Date() : undefined}
            />
          );
        case "BOOLEAN":
          return (
            <input
              disabled={isDisabled}
              type="checkbox"
              name={field.field_name}
              className="master-data-modal-checkbox"
              checked={!!fieldValue}
              onChange={
                isFieldReadOnly
                  ? undefined
                  : (e) =>
                      handleInputChange({
                        target: {
                          name: field.field_name,
                          value: e.target.checked,
                        },
                      })
              }
              style={{ cursor: isFieldReadOnly ? "not-allowed" : "pointer" }}
            />
          );
        case "DECIMAL":
          return (
            <input
              type="number"
              step="0.01"
              name={field.field_name}
              value={fieldValue || ""}
              onChange={isFieldReadOnly ? undefined : handleInputChange}
              className="custom-input"
              disabled={isDisabled}
              readOnly={isFieldReadOnly}
              placeholder={`Enter ${field.title || field.field_name}`}
              style={{
                backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                cursor: isFieldReadOnly ? "not-allowed" : "text",
              }}
            />
          );
        case "IMAGE":
          const pendingImageNames = stagedAttachments
            .filter(
              (attachment) =>
                attachment.scope === "head" &&
                attachment.fieldName === field.field_name,
            )
            .map(
              (attachment) =>
                attachment.file_name || attachment.name || "Image uploaded",
            );
          const pendingImageAttachments = mergeFieldAttachments(
            headAttachmentsByField[field.field_name],
            pendingHeadAttachmentsByField[field.field_name],
          );
          const imageNames = pendingImageNames.length
            ? pendingImageNames
            : selectedFileNames[field.field_name]
              ? Array.isArray(selectedFileNames[field.field_name])
                ? selectedFileNames[field.field_name]
                : [selectedFileNames[field.field_name]]
              : [];
          return (
            <div className="uploaded-image-containers attachment-upload-field">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={
                  isFieldReadOnly
                    ? undefined
                    : (e) => handleImageChange(e, field.field_name)
                }
                className="custom-inputs"
                disabled={isDisabled}
                id="file-input"
              />
              <button
                className={`custom-file-button ${
                  isFieldReadOnly ? "read-only-button" : ""
                }`}
                onClick={
                  isFieldReadOnly
                    ? undefined
                    : () => document.getElementById("file-input").click()
                }
                disabled={isDisabled}
                style={{
                  backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                  cursor: isFieldReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Choose Image
              </button>
              {(pendingImageAttachments.length || imageNames.length > 0) && (
                <div className="attachment-list attachment-list--pending">
                  {(pendingImageAttachments.length
                    ? pendingImageAttachments
                    : imageNames.map((imageName) => ({ file_name: imageName }))
                  ).map((attachment, index) => (
                    <div
                      className="attachment-list__item"
                      key={
                        attachment.id ||
                        attachment.attachment_id ||
                        `${attachment.file_name}-${index}`
                      }
                    >
                      <FileText size={16} />
                      <span
                        className="attachment-list__name"
                        title={attachment.file_name}
                      >
                        {attachment.file_name ||
                          attachment.name ||
                          "Image uploaded"}
                      </span>
                      <button
                        type="button"
                        className="attachment-list__remove"
                        title="Remove this file"
                        onClick={() =>
                          handleRemovePendingAttachment(
                            field.field_name,
                            attachment,
                          )
                        }
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );

        case "file_upload":
        case "FILE_UPLOAD":
          const pendingFileNames = stagedAttachments
            .filter(
              (attachment) =>
                attachment.scope === "head" &&
                attachment.fieldName === field.field_name,
            )
            .map(
              (attachment) =>
                attachment.file_name || attachment.name || "File uploaded",
            );
          const pendingFileAttachments = mergeFieldAttachments(
            headAttachmentsByField[field.field_name],
            pendingHeadAttachmentsByField[field.field_name],
          );
          const fileNames = pendingFileNames.length
            ? pendingFileNames
            : selectedFileNames[field.field_name]
              ? Array.isArray(selectedFileNames[field.field_name])
                ? selectedFileNames[field.field_name]
                : [selectedFileNames[field.field_name]]
              : [];
          return (
            <div className="uploaded-image-containers attachment-upload-field">
              <input
                ref={(el) => (inputRefs.current[field.field_name] = el)}
                type="file"
                multiple
                onChange={
                  isFieldReadOnly
                    ? undefined
                    : (e) => handleFileChange(e, field.field_name)
                }
                className="custom-inputs"
                disabled={isDisabled}
                id={`file-input-${field.field_name}`}
              />
              <button
                className={`custom-file-button ${
                  isFieldReadOnly ? "read-only-button" : ""
                }`}
                onClick={
                  isFieldReadOnly
                    ? undefined
                    : () =>
                        document
                          .getElementById(`file-input-${field.field_name}`)
                          .click()
                }
                disabled={isDisabled}
                style={{
                  backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                  cursor: isFieldReadOnly ? "not-allowed" : "pointer",
                }}
              >
                Choose File
              </button>
              {(pendingFileAttachments.length || fileNames.length > 0) && (
                <div className="attachment-list attachment-list--pending">
                  {(pendingFileAttachments.length
                    ? pendingFileAttachments
                    : fileNames.map((fileName) => ({ file_name: fileName }))
                  ).map((attachment, index) => (
                    <div
                      className="attachment-list__item"
                      key={
                        attachment.id ||
                        attachment.attachment_id ||
                        `${attachment.file_name}-${index}`
                      }
                    >
                      <FileText size={16} />
                      <span
                        className="attachment-list__name"
                        title={attachment.file_name}
                      >
                        {attachment.file_name ||
                          attachment.name ||
                          "File uploaded"}
                      </span>
                      <button
                        type="button"
                        className="attachment-list__remove"
                        title="Remove this file"
                        onClick={() =>
                          handleRemovePendingAttachment(
                            field.field_name,
                            attachment,
                          )
                        }
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );

        case "DROPDOWN":
        case "DROP_DOWN":
          if (!dropdownRefs.current[field.field_name]) {
            dropdownRefs.current[field.field_name] = React.createRef();
          }
          if (!dropdownMenuRefs.current[field.field_name]) {
            dropdownMenuRefs.current[field.field_name] = React.createRef();
          }

          return (
            <div
              className="primary-dropdown-container"
              style={{ position: "relative" }}
            >
              <div
                className={`primary-dropdown-select ${
                  isFieldReadOnly ? "read-only-dropdown" : ""
                }`}
                ref={dropdownRefs.current[field.field_name]}
                onClick={
                  isFieldReadOnly
                    ? undefined
                    : () => handleDropdownOpen(field.field_name, field)
                }
                style={{
                  backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                  cursor: isFieldReadOnly ? "not-allowed" : "pointer",
                }}
              >
                <span style={{ color: !fieldValue ? "#6B7280" : "#444" }}>
                  {fieldValue || `Select ${field.title}`}
                </span>
                <span className="dropdown-icon">▼</span>
              </div>
              {openDropdownField === field.field_name && !isDisabled && (
                <div
                  className="primary-dropdown-options"
                  style={dropdownStyle}
                  ref={dropdownMenuRefs.current[field.field_name]}
                >
                  {(() => {
                    const hasQuery = field.is_query || field.is_db_query;
                    const queryData = queryFieldData[field.field_name] || [];

                    const optionsToRender =
                      isAssignedEngineerField && engineerOptions.length > 0
                        ? engineerOptions
                        : hasQuery
                          ? queryData
                          : field.options
                            ? field.options.split(",")
                            : [];

                    return optionsToRender.map((option, index) => {
                      const isString = typeof option === "string";
                      const value =
                        typeof option === "string"
                          ? option.trim()
                          : option?.Product_Name ||
                            option?.product_name ||
                            option?.name ||
                            option?.company_name ||
                            option?.[field.field_name] ||
                            "";
                      const productId = isString
                        ? ""
                        : option?.id || option?.tran_id || "";

                      return (
                        <div
                          key={index}
                          className="primary-dropdown-option"
                          onClick={() => {
                            handleInputChange({
                              target: {
                                name: field.field_name,
                                value: value,
                              },
                            });
                            if (
                              productId &&
                              (field.field_name === "product_name" ||
                                field.field_name.includes("product"))
                            ) {
                              handleInputChange({
                                target: {
                                  name: "product_id",
                                  value: productId,
                                },
                              });
                            }
                            if (!isString && option) {
                              Object.keys(option).forEach((key) => {
                                if (
                                  key !== field.field_name &&
                                  key !== "id" &&
                                  key !== "tran_id"
                                ) {
                                  const matchingField = publicHeadFields.find(
                                    (f) => f.field_name === key,
                                  );
                                  if (matchingField) {
                                    handleInputChange({
                                      target: {
                                        name: key,
                                        value: option[key],
                                      },
                                    });
                                  }
                                }
                              });
                            }
                            setOpenDropdownField(null);
                            setCurrentQueryField(null);
                          }}
                        >
                          {value}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          );

        default:
          const hasQuery = field.is_query || field.is_db_query;
          const isQueryField = hasQuery && field.db_query && !isFieldReadOnly;

          return (
            <input
              type="text"
              name={field.field_name}
              value={fieldValue || ""}
              onChange={isFieldReadOnly ? undefined : handleInputChange}
              onBlur={
                isFieldReadOnly ? undefined : () => handleLeaveValidation(field)
              }
              className="custom-input"
              disabled={
                isViewMode ||
                (field.field_name === "tran_no" && isAutoTransactionNo) ||
                isFieldReadOnly
              }
              readOnly={isFieldReadOnly}
              placeholder={`Enter ${field.title || field.field_name}`}
              onFocus={
                isFieldReadOnly ? undefined : () => handleEnterValidation(field)
              }
              onClick={isQueryField ? () => handleFieldClick(field) : undefined}
              style={{
                backgroundColor: isFieldReadOnly ? "#f8f9fa" : "",
                cursor: isQueryField
                  ? "pointer"
                  : isFieldReadOnly
                    ? "not-allowed"
                    : "text",
              }}
            />
          );
      }
    };
  }, [
    formData.primaryDetails,
    isViewMode,
    openDropdownField,
    isMaterialRequested,
    isAutoTransactionNo,
    inspectionPopupState,
    formData?.transactionItems,
    queryFieldData,
    engineerOptions,
    popupItems,
    clearedDateFields,
    publicHeadFields,
    selectedFileNames,
    pendingHeadAttachmentsByField,
    headAttachmentsByField,
    stagedAttachments,
  ]);

  useEffect(() => {
    Object.keys(inputRefs.current).forEach((key) => {
      const textarea = inputRefs.current[key]?.current;
      if (textarea && textarea.tagName === "TEXTAREA" && !isViewMode) {
        textarea.style.height = "24px";
        if (textarea.scrollHeight > 24) {
          const newHeight = Math.min(textarea.scrollHeight, 72);
          textarea.style.height = newHeight + "px";
          textarea.style.overflowY =
            textarea.scrollHeight > 72 ? "auto" : "hidden";
        } else {
          textarea.style.overflowY = "hidden";
        }
      }
    });
  }, [formData.primaryDetails, isViewMode]);

  useEffect(() => {
    if (!openDropdownField) return;

    const ref = dropdownRefs.current[openDropdownField];
    const menuRef = dropdownMenuRefs.current[openDropdownField];

    if (
      !ref?.current ||
      !menuRef?.current ||
      !contentRef.current ||
      !containerRef.current
    ) {
      return;
    }

    const triggerElement = ref.current;
    const dropdownMenu = menuRef.current;
    const scrollContainer = contentRef.current;
    const mainContainer = containerRef.current;

    const triggerRect = triggerElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const mainContainerRect = mainContainer.getBoundingClientRect();

    const triggerTop =
      triggerRect.top - containerRect.top + scrollContainer.scrollTop;
    const triggerBottom = triggerTop + triggerElement.offsetHeight;

    const availableSpaceBelow = containerRect.bottom - triggerRect.bottom;
    const availableSpaceAbove = triggerRect.top - containerRect.top;
    const maxAllowedHeight = Math.min(
      mainContainer.offsetHeight * 0.8,
      Math.max(availableSpaceBelow, availableSpaceAbove),
    );

    dropdownMenu.style.maxHeight = `${maxAllowedHeight}px`;

    requestAnimationFrame(() => {
      const actualDropdownHeight = dropdownMenu.offsetHeight;
      const dropdownBottom = triggerBottom + actualDropdownHeight;

      const currentScrollTop = scrollContainer.scrollTop;
      const visibleTop = currentScrollTop;
      const visibleBottom = currentScrollTop + scrollContainer.clientHeight;

      let targetScrollTop = currentScrollTop;

      if (dropdownBottom > visibleBottom) {
        targetScrollTop = dropdownBottom - scrollContainer.clientHeight;
      } else if (triggerBottom < visibleTop) {
        targetScrollTop = triggerTop;
      }

      const maxScroll =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));

      if (Math.abs(targetScrollTop - currentScrollTop) > 5) {
        scrollContainer.scrollTo({
          top: targetScrollTop,
          behavior: "smooth",
        });
      }
    });
  }, [openDropdownField]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".primary-dropdown-container")) {
        setOpenDropdownField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`primary-document-container ${modeHighlight}`}
    >
      <div className="primary-document-header">
        {!isViewMode &&
          !isCopyMode &&
          transactionCode !== "BOM" &&
          transactionCode !== "PL" &&
          type !== "JobCard" &&
          transactionCode !== "drawing-status" && (
            <button className="transaction-btn" onClick={handleTransaction}>
              Select Transaction
            </button>
          )}
        <div className="primary-document-header-title">
          <span className="primary-document-header-icon">
            <FileText size={18} />
          </span>
          <h3>
            {type === "JobCard"
              ? "Job Card Details"
              : "Primary Document Details"}
          </h3>
        </div>
      </div>
      <div ref={contentRef} className="primary-document-content">
        <div className="document-grid">
          {(() => {
            const normalFields = [];
            const textFields = [];
            const inspectionFields = [];
            publicHeadFields.forEach((field) => {
              const isInspection =
                field.field_name === "final_inspection" ||
                field.field_name === "in_process_inspection" ||
                field.field_name === "first_piece_inspection";

              if (isInspection) {
                inspectionFields.push(field);
              } else if (
                field.field_type === "TEXT" ||
                field.field_type === "text"
              ) {
                textFields.push(field);
              } else {
                normalFields.push(field);
              }
            });

            return [...normalFields, ...textFields, ...inspectionFields].map(
              (field) => {
                const {
                  field_name,
                  is_internal_field,
                  is_required,
                  is_read_only_on_edit,
                  title,
                } = field;
                const isEditMode = formData.primaryDetails?.tran_no;
                const isFieldReadOnly = isEditMode && is_read_only_on_edit;

                if (field_name === "create_mr") {
                  return null;
                }

                const labelContent = (
                  <label>
                    {title || field_name}
                    {is_required && !isViewMode && (
                      <span className="required-mark">*</span>
                    )}
                    {isFieldReadOnly && !isViewMode && (
                      <span className="read-only-indicator"> (Read Only)</span>
                    )}
                  </label>
                );

                const fieldComponent = renderedField(field, isFieldReadOnly);

                return is_internal_field ? (
                  <React.Fragment key={field_name}>
                    {fieldComponent}
                  </React.Fragment>
                ) : (
                  <div
                    key={field_name}
                    className={`input-group ${
                      isFieldReadOnly ? "read-only-field" : ""
                    }`}
                  >
                    {labelContent}
                    {fieldComponent}
                  </div>
                );
              },
            );
          })()}
        </div>
      </div>

      {detailButtons?.length > 0 && (
        <div className="extra-details">
          <h6>Other Details *</h6>
          <div className="detail-buttons">
            {detailButtons.map((detail, index) => (
              <button
                key={index}
                className="detail-button"
                onClick={() => handleClick(detail)}
              >
                {detail.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => {
          closeModal();
        }}
        className="transaction-custom-modal"
        overlayClassName="transaction-custom-overlay"
      >
        <SelectTransaction
          onClose={closeModal}
          transactionCode={transactionCode}
        />
      </Modal>
      <Modal
        isOpen={isOtherDetailsModalOpen}
        onRequestClose={closeOtherDetailsModal}
        className="transaction-other-detail-custom-modal"
        overlayClassName="transaction-other-detail-custom-overlay"
      >
        <MasterDataModal
          selectedDetail={matchedHeadFields}
          selectedDetailData={matchedHeadFields.reduce((acc, field) => {
            acc[field.field_name] = formData.primaryDetails[field.field_name];
            return acc;
          }, {})}
          heading="otherDetailsModal"
          extraForms={extraForms}
          modeOfEdit={isViewMode ? "view" : "add"}
          onClose={closeOtherDetailsModal}
        />
      </Modal>
      {showInspectionPopup && (
        <InspectionCardPopup
          selectedFieldName={selectedInspectionFieldName}
          onClose={() => {
            setShowInspectionPopup(false);
          }}
          jobCardNo={formData?.primaryDetails?.tran_no}
          inspectionHeadData={formData?.primaryDetails}
          job_card_id={formData?.primaryDetails?.tran_id}
          jobCardCreated={jobCardCreated}
          setInspectionPopupState={setInspectionPopupState}
          addMode={inspectionPopupState[selectedInspectionFieldName]}
          transactionCode={transactionCode}
        />
      )}

      {isPopupOpen && currentQueryField && (
        <div className="popup-overlay">
          <div className="popup-content" ref={popUpModalRef}>
            <div className="popup-header">
              <h3>
                {(() => {
                  const field = publicHeadFields.find(
                    (f) => f.field_name === currentQueryField,
                  );
                  return field
                    ? `Select ${capitalizeFirstLetterOfEachWord(field.title || field.field_name)}`
                    : "Select Item";
                })()}
              </h3>
              <button
                className="close-button"
                onClick={() => {
                  setIsPopupOpen(false);
                  setCurrentQueryField(null);
                  setPopupSearchQuery("");
                }}
              >
                &times;
              </button>
            </div>
            <div className="search-product">
              <input
                type="text"
                className="search-product-input"
                placeholder="Search..."
                value={popupSearchQuery}
                onChange={(e) => setPopupSearchQuery(e.target.value)}
              />
            </div>
            <ul>
              {popupItems
                .filter((item) => {
                  const searchableText = (
                    item.Product_Name ||
                    item.product_name ||
                    item.company_name ||
                    item.name ||
                    item[currentQueryField] ||
                    ""
                  ).toLowerCase();
                  return searchableText.includes(
                    popupSearchQuery.toLowerCase(),
                  );
                })
                .map((item, index) => {
                  const field = publicHeadFields.find(
                    (f) => f.field_name === currentQueryField,
                  );
                  return (
                    <li
                      key={item.tran_id || item.id || index}
                      onClick={() => handlePopupItemClick(item, field)}
                    >
                      {item.Product_Name ||
                        item.product_name ||
                        item.company_name ||
                        item.name ||
                        item[currentQueryField] ||
                        ""}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrimaryDocument;
